import { create } from 'zustand';
import type { CollaborationUser, CursorPosition } from '@/types/collaboration';

interface UserCursor {
  userId: string;
  position: CursorPosition;
  lastUpdate: number;
}

interface QueryState {
  userId: string;
  query: string;
  cursorPosition: number;
  lastUpdate: number;
}

interface CollaborationState {
  // Users per database
  users: Record<string, CollaborationUser[]>;

  // Cursors per database
  cursors: Record<string, UserCursor[]>;

  // Query state per database
  queryStates: Record<string, QueryState[]>;

  // Actions
  setUsers: (databaseId: string, users: CollaborationUser[]) => void;
  updateCursor: (databaseId: string, userId: string, position: CursorPosition) => void;
  removeCursor: (databaseId: string, userId: string) => void;
  setQueryChange: (databaseId: string, userId: string, query: string, cursorPosition: number) => void;
  clearDatabase: (databaseId: string) => void;
}

export const useCollaborationStore = create<CollaborationState>((set) => ({
  users: {},
  cursors: {},
  queryStates: {},

  setUsers: (databaseId, users) =>
    set((state) => ({
      users: { ...state.users, [databaseId]: users },
    })),

  updateCursor: (databaseId, userId, position) =>
    set((state) => {
      const databaseCursors = state.cursors[databaseId] || [];
      const existingIndex = databaseCursors.findIndex((c) => c.userId === userId);

      const newCursor: UserCursor = {
        userId,
        position,
        lastUpdate: Date.now(),
      };

      let updatedCursors: UserCursor[];
      if (existingIndex >= 0) {
        updatedCursors = [...databaseCursors];
        updatedCursors[existingIndex] = newCursor;
      } else {
        updatedCursors = [...databaseCursors, newCursor];
      }

      return {
        cursors: { ...state.cursors, [databaseId]: updatedCursors },
      };
    }),

  removeCursor: (databaseId, userId) =>
    set((state) => {
      const databaseCursors = state.cursors[databaseId] || [];
      return {
        cursors: {
          ...state.cursors,
          [databaseId]: databaseCursors.filter((c) => c.userId !== userId),
        },
      };
    }),

  setQueryChange: (databaseId, userId, query, cursorPosition) =>
    set((state) => {
      const databaseQueryStates = state.queryStates[databaseId] || [];
      const existingIndex = databaseQueryStates.findIndex((q) => q.userId === userId);

      const newQueryState: QueryState = {
        userId,
        query,
        cursorPosition,
        lastUpdate: Date.now(),
      };

      let updatedQueryStates: QueryState[];
      if (existingIndex >= 0) {
        updatedQueryStates = [...databaseQueryStates];
        updatedQueryStates[existingIndex] = newQueryState;
      } else {
        updatedQueryStates = [...databaseQueryStates, newQueryState];
      }

      return {
        queryStates: { ...state.queryStates, [databaseId]: updatedQueryStates },
      };
    }),

  clearDatabase: (databaseId) =>
    set((state) => {
      const { [databaseId]: _, ...restUsers } = state.users;
      const { [databaseId]: __, ...restCursors } = state.cursors;
      const { [databaseId]: ___, ...restQueryStates } = state.queryStates;

      return {
        users: restUsers,
        cursors: restCursors,
        queryStates: restQueryStates,
      };
    }),
}));
